import { useEffect, useMemo, useState } from 'react';
import styles from './placeSelection.module.css';

export default function PlaceSelection({ setShowSelector, packData, booking, setBooking }) {
    const [selectedPlaceId, setSelectedPlaceId] = useState(booking?.selectedPlaceId || '');

    const [isReserveChecked, setIsReserveChecked] = useState(booking?.amountPaid?.isReserved || false);

    /*
     * ==========================================
     * MATCH PLACES WITH THEIR PRICE
     * ==========================================
     *
     * places:
     * [
     *   { _id: AAA, name: "Mysore Palace" },
     *   { _id: BBB, name: "Zoo" }
     * ]
     *
     * price:
     * [
     *   { placeId: AAA, actual: 1000 },
     *   { placeId: BBB, actual: 5000 }
     * ]
     *
     */

    const placesWithPrice = useMemo(() => {
        const places = packData?.places || [];
        const prices = packData?.price || [];

        return places.map((place) => {
            const placePrice = prices.find((price) => String(price.placeId) === String(place._id));

            return {
                place,
                price: placePrice
            };
        });
    }, [packData?.places, packData?.price]);

    /*
     * ==========================================
     * RESTORE SELECTED PLACE
     * ==========================================
     */

    useEffect(() => {
        if (booking?.selectedPlaceId) {
            setSelectedPlaceId(booking.selectedPlaceId);
        }
    }, [booking?.selectedPlaceId]);

    /*
     * ==========================================
     * SELECT PLACE
     * ==========================================
     */

    const handlePlaceSelect = (place, price) => {
        if (!price) return;

        setSelectedPlaceId(place._id);

        setBooking((prev) => ({
            ...prev,

            selectedPlaceId: place._id,

            place: {
                _id: place._id,
                name: place.name,
                description: place.description
            },

            price: price,

            amountPaid: {
                ...prev.amountPaid,
                reserve: price.reserve || 0
            }
        }));
    };

    /*
     * ==========================================
     * RESERVE CHECKBOX
     * ==========================================
     */

    const handleCheckboxChange = (event) => {
        const checked = event.target.checked;

        setIsReserveChecked(checked);

        setBooking((prev) => ({
            ...prev,

            amountPaid: {
                ...prev.amountPaid,
                isReserved: checked
            }
        }));
    };

    /*
     * ==========================================
     * SELECTED PLACE
     * ==========================================
     */

    const selectedPlaceData = useMemo(() => {
        return placesWithPrice.find((item) => String(item.place?._id) === String(selectedPlaceId));
    }, [placesWithPrice, selectedPlaceId]);

    const selectedPrice = selectedPlaceData?.price || null;

    /*
     * ==========================================
     * NUMBER OF PEOPLE
     * ==========================================
     */

    const people = Number(booking?.people || 1);

    /*
     * ==========================================
     * CALCULATE PRICE
     * ==========================================
     *
     * Example:
     *
     * actual = 5000
     * count = 4
     * perPersonPrice = 1500
     *
     * 1 person  = 5000
     * 2 people  = 5000
     * 3 people  = 5000
     * 4 people  = 5000
     * 5 people  = 6500
     * 6 people  = 8000
     *
     */

    const priceDetails = useMemo(() => {
        if (!selectedPrice) {
            return {
                total: 0,
                pricePerPerson: 0,
                reserveTotal: 0,
                reserve: 0
            };
        }

        const actual = Number(selectedPrice.actual || 0);

        const count = Number(selectedPrice.count || 1);

        const perPersonPrice = Number(selectedPrice.perPersonPrice || 0);

        let total = actual;

        if (people > count) {
            const extraPeople = people - count;

            total = actual + extraPeople * perPersonPrice;
        }

        const pricePerPerson = people > 0 ? total / people : 0;

        const reserve = Number(selectedPrice.reserve || 0);
        const reserveTotal = Number(selectedPrice.reserve || 0) * people;

        return {
            total,
            pricePerPerson,
            reserveTotal,
            reserve
        };
    }, [selectedPrice, people]);

    /*
     * ==========================================
     * STORE CALCULATED PRICE IN BOOKING
     * ==========================================
     */

    useEffect(() => {
        if (!selectedPrice) return;

        setBooking((prev) => ({
            ...prev,

            pricePerPerson: priceDetails.pricePerPerson,

            calculatedPrice: priceDetails.total,

            amountPaid: {
                ...prev.amountPaid,

                reserve: selectedPrice.reserve || 0
            }
        }));
    }, [selectedPrice, priceDetails.pricePerPerson, priceDetails.total, setBooking]);

    /*
     * ==========================================
     * CONTINUE
     * ==========================================
     */

    const handleContinue = () => {
        if (!selectedPrice) return;

        setShowSelector(false);
    };

    /*
     * ==========================================
     * UI
     * ==========================================
     */

    return (
        <div className={styles.tsWrapper}>
            {/* =================================
                CONTENT
            ================================== */}

            <div className={styles.tsContent}>
                {/* HEADER */}

                <div className={styles.tsHeader}>
                    <h2>Select Place</h2>

                    <p>Choose the place you want to visit.</p>

                    {/* RESERVE */}

                    {selectedPrice && Number(selectedPrice.reserve || 0) > 0 && (
                        <div className='reserve-card'>
                            <input
                                type='checkbox'
                                id='reserve'
                                checked={isReserveChecked}
                                onChange={handleCheckboxChange}
                            />
                            <label htmlFor='reserve' className='reserve-content'>
                                <div className='reserve-row'>
                                    <span className='reserve-title'>
                                        Reserve Now and pay the rest later, for any place below
                                        {/* {booking?.reserveTotal
                                        ? Math.round(booking.reserveTotal)
                                        : Math.round(packData?.price?.[0]?.reserve || 0)}
                                    {!booking?.reserveTotal && ' / person'} */}
                                    </span>
                                </div>
                                {/* <div className='reserve-subtitle'>Pay the remaining amount later</div> */}
                            </label>
                        </div>
                    )}
                </div>

                {/* =================================
                    PLACES
                ================================== */}

                <div className={styles.tsCards}>
                    {placesWithPrice.map(({ place, price }) => {
                        const isSelected = String(selectedPlaceId) === String(place._id);

                        return (
                            <div
                                key={place._id}
                                className={`
                                        ${styles.tsCard}
                                        ${isSelected ? styles.tsCardActive : ''}
                                    `}
                                onClick={() => handlePlaceSelect(place, price)}
                            >
                                {/* RADIO */}

                                <div className={styles.tsCardRadio}>
                                    <input
                                        type='radio'
                                        name='selectedPlace'
                                        value={place._id}
                                        checked={isSelected}
                                        onChange={() => handlePlaceSelect(place, price)}
                                    />
                                </div>

                                {/* PLACE CONTENT */}

                                <div className={styles.tsCardLeft}>
                                    {/* NAME */}
                                    <div className={styles.priceDiv}>
                                        <div>
                                            <div className={styles.tsCardTitle}>
                                                <h3>{place.name}</h3>
                                            </div>

                                            {/* DESCRIPTION */}

                                            {place.description && (
                                                <div
                                                    className={styles.tsPlaceDescription}
                                                    dangerouslySetInnerHTML={{
                                                        __html: place.description
                                                    }}
                                                />
                                            )}
                                        </div>
                                        <div className={styles.tsPriceRow}>
                                            <div>
                                                {price.higherPrice && (
                                                    <span className={styles.tsOldPrice}>₹{price.higherPrice}</span>
                                                )}

                                                <span className={styles.tsPrice}>₹{price.actual}</span>

                                                {price.discountPer && (
                                                    <span className={styles.tsDiscount}>{price.discountPer}% OFF</span>
                                                )}
                                            </div>
                                            <div className={styles.priceLimit}>
                                                for up to {price.count} {price.count === 1 ? 'person' : 'persons'}
                                            </div>
                                            {price.perPersonPrice > 0 && (
                                                <div className={styles.priceLimit}>
                                                    ₹{price.perPersonPrice} / extra person
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {/* PRICE */}

                                    {price ? (
                                        <>
                                            {/* NOTE */}

                                            {/* <div className={styles.guideNote}>
                                                <p>
                                                    <span className={styles.noteSpan}>Note:</span> The price is{' '}
                                                    <strong>₹{price.actual}</strong> for up to{' '}
                                                    <strong>{price.count}</strong>{' '}
                                                    {price.count === 1 ? 'person' : 'persons'}. An additional charge of{' '}
                                                    <strong>₹{price.perPersonPrice}</strong> applies for each extra
                                                    person.
                                                </p>
                                            </div> */}
                                            {/* <div className={styles.guideNote}>
                                                <ul>
                                                    <li>
                                                        Up to{' '}
                                                        <strong>
                                                            {price.count} {price.count === 1 ? 'person' : 'persons'}
                                                        </strong>
                                                        : <strong>₹{price.actual}</strong>
                                                    </li>

                                                    <li></li>
                                                </ul>
                                            </div> */}

                                            {/* RESERVE */}

                                            {Number(price.reserve || 0) > 0 && (
                                                <div className={styles.tsReserveRow}>
                                                    <small className={styles.reserveText}>
                                                        Reserve Now at ₹{price.reserve}/person
                                                    </small>
                                                </div>
                                            )}

                                            {/* TERMS */}

                                            {/* {price.terms?.length > 0 && (
                                                <div className={styles.tsTerms}>
                                                    {price.terms.map((term, index) => (
                                                        <p key={index}>✓ {term}</p>
                                                    ))}
                                                </div>
                                            )} */}
                                        </>
                                    ) : (
                                        <p className={styles.noPrice}>Price unavailable</p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* =================================
                FOOTER
            ================================== */}

            <div className={styles.tsFooter}>
                <div className={styles.tsFooterTop}>
                    <div className={styles.tsSummary}>
                        {!selectedPrice ? (
                            <>
                                <span>No place selected</span>

                                <h2>₹0</h2>
                            </>
                        ) : (
                            <>
                                <span>
                                    {people} Traveller
                                    {people > 1 ? 's' : ''}
                                </span>

                                <h2>
                                    ₹{Math.round(isReserveChecked ? priceDetails.reserveTotal : priceDetails.total)}
                                </h2>

                                <small>
                                    ₹{Math.round(isReserveChecked ? priceDetails.reserve : priceDetails.pricePerPerson)}{' '}
                                    / person
                                </small>
                            </>
                        )}
                    </div>
                </div>

                {/* CONTINUE */}

                <button type='button' className={styles.tsContinue} disabled={!selectedPrice} onClick={handleContinue}>
                    Continue
                    {selectedPrice && ` (${people})`}
                </button>
            </div>
        </div>
    );
}
